import {
    DartsGameScore,
    DartsMatchType,
    EventScoreboard,
    IcehockeyPeriodGameScore,
    PairScore,
    PenaltyStatus,
    SnookerScore,
    TeamShootout,
} from '@frontend/sports/betting-offer/feature/model';
import { ceil, times, values } from 'lodash-es';

export class ScoreboardStat {
    constructor(
        public name: string,
        public player1: number | string,
        public player2: number | string,
        public order: number,
        public isVisible: boolean = true, // Controlled from expand/collapse
        public icon: string = '',
        public statClasses: string = '',
    ) {
        this.isEnabled = true;
    }

    isEnabled: boolean; // Is coming from server (LBAI visibility)

    isEmpty(player: 1 | 2): boolean {
        if (player === 1) {
            return this.player1 === -1;
        } else {
            return this.player2 === -1;
        }
    }

    getParsedStat(player: 1 | 2) {
        const value = player === 1 ? this.player1 : this.player2;

        if (typeof value === 'number') {
            return value;
        }

        const parsed = parseInt(value);

        if (isNaN(parsed)) {
            return -1;
        }

        return parsed;
    }
}

export class ScoreboardStatList {
    constructor() {
        this.values = {};
        this.list = [];
    }

    // values and list should not be modified directly.
    values: { [key: string]: ScoreboardStat };
    list: ScoreboardStat[]; // Used for ngFor elements are ordered in it.

    addStat(value: ScoreboardStat): void {
        if (value.name in this.values) {
            this.removeStat(value.name);
        }
        this.values[value.name] = value;
        if (this.list.length === 0 || this.list[this.list.length - 1].order <= value.order) {
            this.list.push(value);
        } else {
            for (let i = 0, cnt = this.list.length; i < cnt; i++) {
                if (this.list[i].order > value.order) {
                    this.list.splice(i, 0, value);

                    return;
                }
            }
        }
    }

    private findIndex(key: string): number {
        for (let i = 0; i < this.list.length; i++) {
            if (this.list[i].name === key) {
                return i;
            }
        }

        return -1;
    }

    updateStatValue(
        key: string,
        newValue: {
            player1?: number;
            player2?: number;
            isVisible?: boolean;
            icon?: string;
        },
    ): void {
        if (key in this.values) {
            const stat = this.values[key];
            if (newValue.player1 !== undefined) {
                stat.player1 = newValue.player1;
            }
            if (newValue.player2 !== undefined) {
                stat.player2 = newValue.player2;
            }
            if (newValue.icon !== undefined) {
                stat.icon = newValue.icon;
            }
            if (newValue.isVisible !== undefined) {
                stat.isVisible = newValue.isVisible;
            }
        }
    }

    setIsEnabled(key: string, isEnabled: boolean): void {
        if (key in this.values) {
            const stat1 = this.values[key];
            stat1.isEnabled = isEnabled;
        }
    }

    removeStat(key: string): void {
        const indx = this.findIndex(key);
        if (indx > -1) {
            this.list.splice(indx, 1);
            delete this.values[key];
        }
    }
}

export class PairGameScoreboardStats extends ScoreboardStatList {
    constructor() {
        super();
    }

    setStats(
        statsKey: string,
        order: number,
        periodScore: PairScore<number> | undefined,
        icon: string,
        isVisible: boolean = true,
        classes: string = '',
    ): void {
        if (periodScore) {
            this.addStat(new ScoreboardStat(statsKey, periodScore.participant1, periodScore.participant2, order, isVisible, icon, classes));
        } else {
            const stat = new ScoreboardStat(statsKey, 0, 0, order, false, icon, classes);
            stat.isEnabled = false;
            this.addStat(stat);
        }
    }

    updateStatValueFromDoubleCounter(statsKey: string, newPeriodScore: PairScore<number> | undefined): void {
        if (newPeriodScore) {
            this.updateStatValue(statsKey, {
                player1: newPeriodScore.participant1,
                player2: newPeriodScore.participant2,
            });
            this.setIsEnabled(statsKey, true);
        } else {
            this.setIsEnabled(statsKey, false);
        }
    }

    updateStatVisibility(statsKey: string, isVisible: boolean): void {
        this.updateStatValue(statsKey, { isVisible });
    }
}

export class PeriodGameFullScoreboardStats extends ScoreboardStatList {
    isActive = false;
    periods: ScoreboardStatList;
    points: ScoreboardStat | null = null;

    constructor() {
        super();
        this.periods = new ScoreboardStatList();
    }
}

export class SnookerScoreboardStats extends PairGameScoreboardStats {
    constructor() {
        super();
    }

    currentlyServing: number | undefined;
    player1CurrentScore: number;
    player2CurrentScore: number;
    participant1: number;
    participant2: number;

    fillSnookerStats(scoreboard: EventScoreboard): void {
        const score = scoreboard.getScore<SnookerScore>();
        if (!score) {
            return;
        }

        this.player1CurrentScore = score.participant1;
        this.player2CurrentScore = score.participant2;
        this.participant1 = score.frames.participant1;
        this.participant2 = score.frames.participant2;
        this.currentlyServing = score.currentlyServing;
    }
}

export class PenaltyShootoutScoreboardStats extends PairGameScoreboardStats {
    constructor() {
        super();
        this.penalties = [];
        this.penaltyScore = [];
    }

    penalties: TeamShootout[];
    penaltyScore: number[];

    setPenalties(score?: IcehockeyPeriodGameScore): void {
        if (!score) {
            return;
        }

        this.penalties = this.getPenaltyScore(score);
        this.penaltyScore = [
            this.penalties[0].penalties.filter((status) => status === PenaltyStatus.Scored).length,
            this.penalties[1].penalties.filter((status) => status === PenaltyStatus.Scored).length,
        ];
    }

    private getPenaltyScore(score: IcehockeyPeriodGameScore): TeamShootout[] {
        if (!score || !score.penaltyShootOutScore) {
            return [];
        }
        const visible = 5;

        const teams = values(score.penaltyShootOutScore);
        const offset = ceil(teams[0].length / visible - 1) * visible;

        return teams.map((team) => ({
            points: team.filter((penalty) => penalty.status === PenaltyStatus.Scored).length,
            penalties: times(visible).map((_penalty, index) => (team[index + offset] ? team[index + offset].status : PenaltyStatus.Unset)),
        }));
    }
}

export class DartsScoreboardStats extends PairGameScoreboardStats {
    participant1: number;
    participant2: number;
    hundredEighties: PairScore<number>;
    legs: PairScore<number>;

    currentlyServing?: number;
    matchType: DartsMatchType;

    fillDartsStats(scoreboard: EventScoreboard): void {
        const score = scoreboard.getScore<DartsGameScore>();

        if (!score) {
            return;
        }

        this.participant1 = score.participant1;
        this.participant2 = score.participant2;
        this.currentlyServing = score.currentlyServing;
        this.matchType = score.matchType;

        this.hundredEighties = score.hundredEighties;
        this.legs = score.legs;
    }
}

export class SetGameScoreboardStats {
    constructor() {
        this.sets = new ScoreboardStatList();
        this.turn = new ScoreboardStat('turn', 0, 0, 0, false);
        this.points = null;
        this.isActive = false;
    }

    sportId: number;
    isActive: boolean;

    turn: ScoreboardStat;
    points: ScoreboardStat | null;
    sets: ScoreboardStatList;

    private isSetFinished(indx: number): boolean {
        if (indx === this.sets.list.length - 1) {
            return !this.isActive;
        }

        return +this.sets.list[indx + 1].player1 > -1 || +this.sets.list[indx + 1].player2 > -1;
    }

    isHighlighedSet(indx: number, player: number): boolean {
        if (indx < 0 || indx >= this.sets.list.length) {
            return false;
        }
        const isFinished = this.isSetFinished(indx);
        if (!isFinished) {
            return true;
        }

        return (
            (player === 1 && this.sets.list[indx].player1 > this.sets.list[indx].player2) ||
            (player === 2 && this.sets.list[indx].player1 < this.sets.list[indx].player2) ||
            (this.sets.list[indx].player1 === this.sets.list[indx].player2 && +this.sets.list[indx].player1 > -1)
        );
    }
}
